// Admin console (also the development simulator).
//
// On a street hub: the admin signs in with the admin password; the page then
// connects with that session and shows approvals, houses and Pis
// (admin-street.ts). On a development hub (no street set up): room keys, and
// simulated houses. Add ?sim=1 to show simulated houses on a street hub too.
//
// - Connects to the hub as a controller to send shows and set per-house offsets.
// - Plays song files and keeps the shared beat clock on each song's beats
//   (worked out in advance), or listens through the microphone.
// - Runs simulated houses: each is the real AgentClient (agent/core.ts) with a
//   driver that paints bulbs on the page instead of sending UDP to lights.
//   "Light lag" models a slow strand; "Unplug internet" cuts the hub
//   connection to show the house keeps running its last show.
//
// Built with esbuild into dist/admin.js (npm run build:web).

import { AgentClient } from "../agent/core.ts";
import type { Output, OutputDriver } from "../agent/driver.ts";
import { effectOf, isBeatShow, type Frame } from "../shared/effects.ts";
import {
  LIMITS,
  type BeatShowSpec,
  type ClientMessage,
  type DeviceInfo,
  type EffectName,
  type HouseView,
  type Rate,
  type Rgb,
  type Show,
  type ShowSpec,
} from "../shared/protocol.ts";
import { parseServerMessage } from "../shared/validate.ts";
import type { LibraryListing, SavedAnalysis } from "../shared/library.ts";
import { analyzeAudio } from "./analyze.ts";
import { fitBeatGrid } from "./beats.ts";
import { LibraryClient, LibraryError } from "./library-client.ts";
import { BeatListener, type BeatReading } from "./listen.ts";
import { mixdown, SongPlayer, type Track } from "./player.ts";
import { setupSchedule } from "./admin-schedule.ts";
import { refreshSoon, setupStreetAdmin } from "./admin-street.ts";
import { HALLOWEEN_COLORS, HALLOWEEN_PALETTES } from "./palettes.ts";
import { api, getMe, HALLOWEEN, setupHeader } from "./site.ts";

// True when this hub has a street and we're signed in as its admin.
let streetMode = false;

// ---------------------------------------------------------------------------
// Settings

interface Settings {
  hub: string;
  room: string;
  controlKey: string;
  agentKey: string;
}

const STORAGE_KEY = "craftsman-christmas";

function loadSettings(): Settings {
  const defaults: Settings = {
    hub: `${location.protocol === "https:" ? "wss" : "ws"}://${location.host || "localhost:8787"}`,
    room: "dev",
    controlKey: "dev-control",
    agentKey: "dev-agent",
  };
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}") };
  } catch {
    return defaults;
  }
}

function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Private window or blocked storage: settings just won't persist.
  }
}

let settings = loadSettings();

// ---------------------------------------------------------------------------
// DOM helpers

function $<T extends HTMLElement>(selector: string, root: ParentNode = document): T {
  const el = root.querySelector<T>(selector);
  if (!el) throw new Error(`missing element ${selector}`);
  return el;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  node.append(...children);
  return node;
}

const css = (c: Rgb) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`;

function hexToRgb(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function ago(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

// ---------------------------------------------------------------------------
// Controller connection

let controller: WebSocket | null = null;
let controllerRetry: ReturnType<typeof setTimeout> | null = null;
let hubShow: Show | null = null;

function setHubStatus(text: string, cls: "" | "ok" | "bad"): void {
  const pill = $("#hub-status");
  pill.textContent = text;
  pill.className = `pill ${cls}`;
}

function connectController(): void {
  if (controllerRetry) clearTimeout(controllerRetry);
  controllerRetry = null;
  const old = controller;
  controller = null;
  old?.close();

  let ws: WebSocket;
  try {
    ws = new WebSocket(settings.hub);
  } catch {
    setHubStatus("bad hub URL", "bad");
    return;
  }
  controller = ws;
  setHubStatus("connecting…", "");

  ws.addEventListener("open", () => {
    sendControl(
      streetMode
        ? { type: "hello", role: "controller", room: "street" }
        : { type: "hello", role: "controller", room: settings.room, key: settings.controlKey },
    );
  });
  ws.addEventListener("message", (event) => {
    if (typeof event.data !== "string") return;
    let msg;
    try {
      msg = parseServerMessage(event.data);
    } catch {
      return;
    }
    switch (msg.type) {
      case "welcome":
        setHubStatus(streetMode ? "connected" : `connected · room ${settings.room}`, "ok");
        if (msg.show) applyHubShow(msg.show);
        void loadLibrary();
        if (player.current) announceNowPlaying(); // reconnected mid-song
        break;
      case "show":
        applyHubShow(msg.show);
        break;
      case "houses":
        renderHouses(msg.houses);
        if (streetMode) refreshSoon();
        break;
      case "error":
        setHubStatus(msg.message, "bad");
        break;
    }
  });
  ws.addEventListener("close", (event) => {
    if (controller !== ws) return;
    controller = null;
    if (event.code === 4003) {
      setHubStatus(streetMode ? "signed out" : "wrong room or control key", "bad");
      if (streetMode) location.reload();
      return;
    }
    setHubStatus("disconnected · retrying", "bad");
    controllerRetry = setTimeout(connectController, 2000);
  });
}

function sendControl(msg: ClientMessage): void {
  if (controller?.readyState === WebSocket.OPEN) controller.send(JSON.stringify(msg));
}

// ---------------------------------------------------------------------------
// Show controls

const SOLID_COLORS: [string, Rgb][] = [
  ...(HALLOWEEN ? HALLOWEEN_COLORS : []),
  ["Red", [255, 0, 0]],
  ["Green", [0, 200, 40]],
  ["Blue", [0, 60, 255]],
  ["Warm white", [255, 170, 80]],
  ["Gold", [255, 140, 0]],
  ["Purple", [140, 0, 255]],
];

const PALETTES: [string, Rgb[]][] = [
  ...(HALLOWEEN ? HALLOWEEN_PALETTES : []),
  ["Classic", [[255, 0, 0], [0, 200, 40]]],
  ["Candy cane", [[255, 0, 0], [255, 255, 255]]],
  ["Icy", [[0, 60, 255], [200, 230, 255], [0, 200, 255]]],
  ["Gold and white", [[255, 140, 0], [255, 220, 170]]],
  ["Rainbow", [[255, 0, 0], [255, 140, 0], [255, 230, 0], [0, 200, 40], [0, 60, 255], [140, 0, 255]]],
];

const EFFECT_NAMES: Record<EffectName, string> = {
  step: "Color step",
  chase: "Street chase",
  pulse: "Soft pulse",
  flash: "Hard flash",
  strobe: "Strobe",
  run: "Run",
  comet: "Comet",
  marquee: "Marquee",
  bounce: "Bounce",
  crossrun: "Crossing lights",
  fill: "Fill",
  alternate: "Alternate",
  twinkle: "Twinkle",
  rainbow: "Rainbow flow",
};

// What strands without sections show instead (see shared/effects.ts).
const SUBSTITUTES: Partial<Record<EffectName, string>> = {
  run: "a hard flash as the run passes",
  comet: "a hard flash as the comet passes",
  marquee: "quick strobe flashes as the chasers pass",
  bounce: "a hard flash as the light bounces past",
  crossrun: "a hard flash as either light passes",
  fill: "a soft pulse",
  alternate: "color steps",
  twinkle: "a gentle pulse",
  rainbow: "slow color changes",
};

const RATE_NAMES: Record<Rate, string> = { 0.25: "quarter", 0.5: "half", 1: "normal", 2: "double" };

type Mode = "off" | "solid" | EffectName;

const ui = {
  mode: "solid" as Mode,
  solid: SOLID_COLORS[0]![1],
  palette: PALETTES[0]![1],
  bpm: 100,
  // The speed the admin picked.
  rate: 1 as Rate,
  brightness: 80,
};

const isEffect = (mode: Mode): mode is EffectName => mode !== "off" && mode !== "solid";

// Song playback and beat detection need an effect; Off and Solid become
// color steps.
function beatEffect(): EffectName {
  if (!isEffect(ui.mode)) ui.mode = "step";
  return ui.mode;
}

// The selected effect at a tempo. Color step and chase at normal speed go out
// as the original beat and chase shows, which Pis that haven't updated yet follow.
function effectSpec(effect: EffectName, bpm: number, startEpochMs?: number): BeatShowSpec {
  const rate = ui.rate;
  const base = { bpm, palette: ui.palette, brightness: ui.brightness, ...(startEpochMs === undefined ? {} : { startEpochMs }) };
  if (rate === 1 && effect === "step") return { kind: "beat", ...base };
  if (rate === 1 && effect === "chase") return { kind: "chase", ...base };
  return { kind: "effect", effect, rate, ...base };
}

function sameEffect(a: BeatShowSpec, b: BeatShowSpec): boolean {
  const x = effectOf(a);
  const y = effectOf(b);
  return x.effect === y.effect && x.rate === y.rate;
}

function sendShow(keepPhase: boolean): void {
  let spec: ShowSpec;
  if (ui.mode === "off") {
    spec = { kind: "off" };
  } else if (ui.mode === "solid") {
    spec = { kind: "solid", rgb: ui.solid, brightness: ui.brightness };
  } else {
    const beatSpec = effectSpec(ui.mode, ui.bpm);
    // Effect, speed, palette and brightness changes keep the beat where it is.
    const cur = hubShow;
    if (keepPhase && cur && isBeatShow(cur) && cur.bpm === ui.bpm) {
      beatSpec.startEpochMs = cur.startEpochMs ?? cur.t;
    }
    spec = beatSpec;
  }
  sendControl({ type: "set_show", show: spec });
}

function applyHubShow(show: Show): void {
  hubShow = show;
  if (show.kind === "solid") ui.solid = show.rgb;
  if (isBeatShow(show)) {
    const { effect, rate } = effectOf(show);
    ui.mode = effect;
    ui.rate = rate as Rate;
    ui.palette = show.palette;
    ui.bpm = show.bpm;
    $<HTMLInputElement>("#bpm").value = String(show.bpm);
  } else {
    ui.mode = show.kind;
  }
  if (show.kind !== "off") {
    ui.brightness = show.brightness;
    $<HTMLInputElement>("#brightness").value = String(show.brightness);
    $("#brightness-value").textContent = `${show.brightness}%`;
  }
  renderShowControls();
}

function swatch(label: string, colors: Rgb[], pressed: boolean, onClick: () => void): HTMLButtonElement {
  const b = el("button", {
    class: `swatch${colors.length === 1 ? " single" : ""}`,
    title: label,
    "aria-label": label,
    "aria-pressed": String(pressed),
  });
  for (const c of colors) b.append(el("span", { style: `background:${css(c)}` }));
  b.addEventListener("click", onClick);
  return b;
}

const sameColors = (a: Rgb[], b: Rgb[]) =>
  a.length === b.length && a.every((c, i) => c.every((v, j) => v === b[i]?.[j]));

function renderShowControls(): void {
  for (const b of document.querySelectorAll<HTMLButtonElement>("[data-mode]")) {
    b.setAttribute("aria-pressed", String(b.dataset.mode === ui.mode));
  }
  // Rows show for "off", "solid" or any "effect".
  const group = isEffect(ui.mode) ? "effect" : ui.mode;
  for (const row of document.querySelectorAll<HTMLElement>("[data-for]")) {
    row.hidden = !row.dataset.for!.split(" ").includes(group);
  }
  for (const b of document.querySelectorAll<HTMLButtonElement>("[data-rate]")) {
    b.setAttribute("aria-pressed", String(Number(b.dataset.rate) === ui.rate));
  }
  const notes: string[] = [];
  if (isEffect(ui.mode)) {
    const substitute = SUBSTITUTES[ui.mode];
    if (substitute) notes.push(`Needs real-time lights; other strands show ${substitute}.`);
  }
  $("#effect-note").textContent = notes.join(" ");
  $("#swatches").replaceChildren(
    ...SOLID_COLORS.map(([name, c]) =>
      swatch(name, [c], sameColors([c], [ui.solid]), () => {
        ui.solid = c;
        sendShow(false);
      }),
    ),
  );
  $("#palettes").replaceChildren(
    ...PALETTES.map(([name, p]) =>
      swatch(name, p, sameColors(p, ui.palette), () => {
        ui.palette = p;
        sendShow(true);
      }),
    ),
  );
  let summary = "none yet";
  if (hubShow && isBeatShow(hubShow)) {
    const { effect, rate } = effectOf(hubShow);
    summary = `${EFFECT_NAMES[effect]}${rate === 1 ? "" : `, ${RATE_NAMES[rate as Rate]} speed`} · ${hubShow.bpm} BPM`;
  } else if (hubShow) {
    summary = hubShow.kind;
  }
  $("#show-summary").textContent = `Hub show: ${summary}`;
}

function setupShowControls(): void {
  for (const b of document.querySelectorAll<HTMLButtonElement>("[data-mode]")) {
    b.addEventListener("click", () => {
      ui.mode = b.dataset.mode as Mode;
      if (!isEffect(ui.mode)) stopListening();
      renderShowControls();
      sendShow(isEffect(ui.mode));
    });
  }
  for (const b of document.querySelectorAll<HTMLButtonElement>("[data-rate]")) {
    b.addEventListener("click", () => {
      ui.rate = Number(b.dataset.rate) as Rate;
      renderShowControls();
      if (isEffect(ui.mode)) sendShow(true);
    });
  }
  $<HTMLInputElement>("#custom-color").addEventListener("change", (e) => {
    ui.solid = hexToRgb((e.target as HTMLInputElement).value);
    sendShow(false);
  });

  const brightness = $<HTMLInputElement>("#brightness");
  brightness.addEventListener("input", () => {
    $("#brightness-value").textContent = `${brightness.value}%`;
  });
  brightness.addEventListener("change", () => {
    ui.brightness = Number(brightness.value);
    sendShow(true);
  });

  const bpm = $<HTMLInputElement>("#bpm");
  bpm.addEventListener("change", () => {
    const value = Math.round(Number(bpm.value) * 10) / 10;
    ui.bpm = Math.min(LIMITS.maxBpm, Math.max(LIMITS.minBpm, Number.isFinite(value) ? value : 100));
    bpm.value = String(ui.bpm);
    renderShowControls();
    sendShow(false);
  });

  // Tap tempo: average up to 16 taps; beat 0 lands on the last tap. Keep one
  // decimal place, since a rounded BPM drifts off the music within a minute.
  let taps: number[] = [];
  let tapSend: ReturnType<typeof setTimeout> | null = null;
  $("#tap").addEventListener("click", () => {
    stopListening(); // tapping takes over from the microphone
    const now = Date.now();
    if (taps.length && now - taps[taps.length - 1]! > 2000) taps = [];
    taps.push(now);
    taps = taps.slice(-16);
    if (taps.length < 3) return;
    const interval = (taps[taps.length - 1]! - taps[0]!) / (taps.length - 1);
    const tapped = Math.round(600000 / interval) / 10;
    ui.bpm = Math.min(LIMITS.maxBpm, Math.max(LIMITS.minBpm, tapped));
    bpm.value = String(ui.bpm);
    if (tapSend) clearTimeout(tapSend);
    tapSend = setTimeout(() => {
      const effect = beatEffect();
      renderShowControls();
      sendControl({ type: "set_show", show: effectSpec(effect, ui.bpm, taps[taps.length - 1]!) });
    }, 700);
  });
}

// ---------------------------------------------------------------------------
// Listening: microphone beat detection drives the shared beat clock

// Below these, the reading is treated as "no steady beat" (tuned on synthetic
// audio in test/beat.test.ts; keep the two in step).
const MIN_CONFIDENCE = 0.2;
const MIN_STRENGTH = 0.2;
const SILENT_LEVEL = 0.002;
// Correct the broadcast beat only when it is off by more than this, and not
// more often than every MIN_RESEND_MS, so the lights do not jump around.
const MAX_PHASE_ERROR_MS = 40;
const MAX_TEMPO_ERROR = 0.005;
const MIN_RESEND_MS = 2000;
// A correction must show up in this many readings in a row (~0.5 s apart)
// before it is sent, so a detector wavering between the beat and the
// off-beat does not make the lights jump back and forth.
const AGREEING_READINGS = 3;

let listener: BeatListener | null = null;
let tempoScale = 1;
let lastAutoSend = 0;
let candidate: { bpm: number; beatEpochMs: number; count: number } | null = null;

// Signed distance (ms) from a beat at `at` to the nearest beat of a grid.
function offGrid(at: number, gridStart: number, bpm: number): number {
  const beatMs = 60000 / bpm;
  let d = (at - gridStart) % beatMs;
  if (d < 0) d += beatMs;
  return d > beatMs / 2 ? d - beatMs : d;
}

function setListenStatus(text: string): void {
  $("#listen-status").textContent = text;
}

async function startListening(): Promise<void> {
  if (listener) return;
  player.stop();
  renderSongs();
  const next = new BeatListener(onReading);
  listener = next;
  renderListenControls();
  setListenStatus("Starting microphone…");
  try {
    await next.start();
    setListenStatus("Listening… waiting for a steady beat");
  } catch (e) {
    next.stop();
    if (listener === next) listener = null;
    renderListenControls();
    const err = e as Error;
    setListenStatus(
      err.name === "NotAllowedError"
        ? "Microphone blocked. Allow it for this page (and for your browser in macOS System Settings > Privacy > Microphone)."
        : `Could not start the microphone: ${err.message}`,
    );
  }
}

function stopListening(): void {
  if (!listener) return;
  listener.stop();
  listener = null;
  renderListenControls();
  setListenStatus("Stopped. The lights keep the last beat.");
  $<HTMLElement>("#level > span").style.width = "0";
}

function renderListenControls(): void {
  const on = listener !== null;
  $("#listen").textContent = on ? "Stop listening" : "Listen with microphone";
  $("#listen").setAttribute("aria-pressed", String(on));
  $("#tempo-half").hidden = !on;
  $("#tempo-double").hidden = !on;
  $("#tempo-half").setAttribute("aria-pressed", String(tempoScale === 0.5));
  $("#tempo-double").setAttribute("aria-pressed", String(tempoScale === 2));
}

function onReading(r: BeatReading): void {
  $<HTMLElement>("#level > span").style.width = `${Math.min(100, Math.sqrt(r.level) * 300)}%`;
  const est = r.estimate;
  if (r.level < SILENT_LEVEL) {
    setListenStatus("Too quiet. Turn the music up or move the computer closer to the speaker.");
    return;
  }
  if (!est || r.lastBeatEpochMs === null || est.confidence < MIN_CONFIDENCE || est.strength < MIN_STRENGTH) {
    setListenStatus("Listening… no steady beat yet (the lights keep the last one)");
    return;
  }

  // Fold into the accepted tempo range.
  let bpm = est.bpm * tempoScale;
  while (bpm > LIMITS.maxBpm) bpm /= 2;
  while (bpm < LIMITS.minBpm) bpm *= 2;
  bpm = Math.round(bpm * 10) / 10;
  setListenStatus(`${bpm} BPM · confidence ${Math.round(est.confidence * 100)}%`);

  const beat = r.lastBeatEpochMs;
  const cur = hubShow;
  if (cur && isBeatShow(cur)) {
    const tempoError = Math.abs(cur.bpm - bpm) / bpm;
    const phaseError = offGrid(beat, cur.startEpochMs ?? cur.t, cur.bpm);
    if (tempoError <= MAX_TEMPO_ERROR && Math.abs(phaseError) <= MAX_PHASE_ERROR_MS) {
      candidate = null; // the lights are already on the beat
      return;
    }
  }
  const agrees =
    candidate !== null &&
    Math.abs(candidate.bpm - bpm) / bpm <= MAX_TEMPO_ERROR &&
    Math.abs(offGrid(beat, candidate.beatEpochMs, candidate.bpm)) <= MAX_PHASE_ERROR_MS;
  candidate = agrees ? { bpm, beatEpochMs: beat, count: candidate!.count + 1 } : { bpm, beatEpochMs: beat, count: 1 };

  const now = Date.now();
  if (candidate.count < AGREEING_READINGS || now - lastAutoSend < MIN_RESEND_MS) return;
  candidate = null;
  lastAutoSend = now;
  const effect = beatEffect();
  ui.bpm = bpm;
  $<HTMLInputElement>("#bpm").value = String(bpm);
  sendControl({ type: "set_show", show: effectSpec(effect, bpm, Math.round(beat)) });
}

function setupListenControls(): void {
  $("#listen").addEventListener("click", () => {
    if (listener) stopListening();
    else void startListening();
  });
  const scale = (value: number) => {
    tempoScale = tempoScale === value ? 1 : value;
    lastAutoSend = 0; // apply as soon as readings agree
    candidate = null;
    renderListenControls();
  };
  $("#tempo-half").addEventListener("click", () => scale(0.5));
  $("#tempo-double").addEventListener("click", () => scale(2));
  renderListenControls();

  // Pulse a dot on each beat of the show the hub is broadcasting.
  const dot = $("#beat-dot");
  let lastBeat = -1;
  const frame = () => {
    const show = hubShow;
    if (show && isBeatShow(show) && Date.now() >= show.t) {
      const beat = Math.floor((Date.now() - (show.startEpochMs ?? show.t)) / (60000 / show.bpm));
      if (beat !== lastBeat) {
        lastBeat = beat;
        dot.classList.add("on");
        setTimeout(() => dot.classList.remove("on"), 90);
      }
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

// ---------------------------------------------------------------------------
// Song library: songs live on the hub, are analyzed once ahead of time, and
// play here while the page broadcasts each song's known beats

// Resend the beat clock when the lights would be further than this from the
// song's real beats over the next few seconds. Tracked beats wobble by a frame
// or two (~10-20 ms), so allow some slack.
const SONG_MAX_ERROR_MS = 35;
const SONG_LOOKAHEAD_MS = 4000;
const SONG_MIN_RESEND_MS = 1500;

const library = new LibraryClient(() => settings);
const player = new SongPlayer((track) => void playAfter(track.id));
let songs: LibraryListing[] = [];
// Full analyses of songs about to play (the list only has summaries).
const analyses = new Map<string, SavedAnalysis>();
// Per-song work in progress or errors, by song id.
const songState = new Map<string, string>();
// Decoded audio for the playing and next song only (decoded songs are large).
const buffers = new Map<string, AudioBuffer>();
let libraryNote = "";
let lastSongSend = 0;
let analyzingAll = false;

function formatTime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

async function loadLibrary(): Promise<void> {
  try {
    songs = await library.list();
    libraryNote = "";
  } catch (e) {
    songs = [];
    libraryNote =
      e instanceof LibraryError && e.status === 404
        ? "This hub has no song library (set MUSIC_DIR on the hub)."
        : `Could not load the song library: ${(e as Error).message}`;
  }
  renderSongs();
}

// Fetch and decode a song, analyzing and saving its beats if not done yet.
async function prepare(song: LibraryListing): Promise<AudioBuffer> {
  const cached = buffers.get(song.id);
  if (cached && analyses.has(song.id)) return cached;
  songState.set(song.id, song.analysis ? "loading…" : "finding the beats…");
  renderSongs();
  try {
    const buffer = await player.decode(await library.audio(song.id));
    if (!song.analysis) {
      await new Promise((r) => setTimeout(r, 30)); // let the list repaint first
      const analysis = analyzeAudio(mixdown(buffer), buffer.sampleRate);
      if (!analysis || analysis.beats.length < 8) throw new Error("no steady beat found in this song");
      await library.saveAnalysis(song.id, analysis);
      song.analysis = { bpm: analysis.bpm, duration: analysis.duration };
      analyses.set(song.id, analysis);
    } else if (!analyses.has(song.id)) {
      analyses.set(song.id, await library.analysis(song.id));
    }
    buffers.set(song.id, buffer);
    songState.delete(song.id);
    return buffer;
  } catch (e) {
    songState.set(
      song.id,
      e instanceof DOMException
        ? "error: can't read this file (copy-protected or unsupported format)"
        : `error: ${(e as Error).message}`,
    );
    throw e;
  } finally {
    renderSongs();
  }
}

async function analyzeAll(): Promise<void> {
  if (analyzingAll) return;
  analyzingAll = true;
  renderSongs();
  for (const song of songs.filter((s) => !s.analysis)) {
    try {
      await prepare(song);
      if (player.current?.track.id !== song.id) buffers.delete(song.id);
    } catch {
      // Shown on the song's row; carry on with the rest.
    }
  }
  analyzingAll = false;
  renderSongs();
}

async function playSong(song: LibraryListing): Promise<void> {
  stopListening();
  let buffer: AudioBuffer;
  try {
    buffer = await prepare(song);
  } catch {
    return;
  }
  player.play({ id: song.id, title: song.title, analysis: analyses.get(song.id)! }, buffer);
  announceNowPlaying();
  void keepAwake(true);
  for (const id of buffers.keys()) if (id !== song.id) buffers.delete(id);
  syncSongBeat(true);
  renderSongs();
  // Get the next song ready while this one plays.
  const next = songAfter(song.id);
  if (next && next.id !== song.id) void prepare(next).catch(() => {});
}

// Songs the playlist plays through: turned on and readable.
function playable(song: LibraryListing): boolean {
  return song.enabled && !songState.get(song.id)?.startsWith("error");
}

function songAfter(id: string): LibraryListing | undefined {
  const i = songs.findIndex((s) => s.id === id);
  return songs.slice(i + 1).find(playable);
}

async function playAfter(id: string): Promise<void> {
  let next = songAfter(id);
  // Loop: after the last song, start again from the top.
  if (!next && $<HTMLInputElement>("#song-loop").checked) {
    next = songs.find(playable);
  }
  if (next) await playSong(next);
  else stopSong();
}

// Keep this screen awake while a show plays: a sleeping laptop or phone would
// stop the music and the street's beat.
let wakeLock: { release(): Promise<void> } | null = null;
async function keepAwake(on: boolean): Promise<void> {
  const nav = navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<{ release(): Promise<void> }> } };
  if (on && !wakeLock && nav.wakeLock && document.visibilityState === "visible") {
    wakeLock = await nav.wakeLock.request("screen").catch(() => null);
  } else if (!on && wakeLock) {
    await wakeLock.release().catch(() => {});
    wakeLock = null;
  }
}
document.addEventListener("visibilitychange", () => {
  // The browser drops the lock when the tab is hidden; take it back on return.
  if (document.visibilityState === "visible" && player.current) {
    wakeLock = null;
    void keepAwake(true);
  }
});

// Tell the hub what's playing, for the public Listen page.
function announceNowPlaying(): void {
  const playing = player.current;
  sendControl({
    type: "now_playing",
    song: playing
      ? {
          songId: playing.track.id,
          title: playing.track.title,
          startEpochMs: Math.round(playing.startEpochMs),
          duration: playing.track.analysis.duration,
        }
      : null,
  });
}

function stopSong(): void {
  const wasPlaying = player.current !== null;
  player.stop();
  if (wasPlaying) announceNowPlaying();
  void keepAwake(false);
  // Hold a steady color instead of beating on in silence.
  if (wasPlaying) {
    sendControl({ type: "set_show", show: { kind: "solid", rgb: ui.palette[0]!, brightness: ui.brightness } });
  }
  renderSongs();
}

async function moveSong(id: string, delta: number): Promise<void> {
  const i = songs.findIndex((s) => s.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= songs.length) return;
  [songs[i], songs[j]] = [songs[j]!, songs[i]!];
  renderSongs();
  try {
    await library.savePlaylist(songs.map((s) => s.id));
  } catch (e) {
    libraryNote = `Could not save the order: ${(e as Error).message}`;
    renderSongs();
  }
}

async function setSongEnabled(song: LibraryListing, enabled: boolean): Promise<void> {
  try {
    await library.setEnabled(song.id, enabled);
    song.enabled = enabled;
  } catch (e) {
    libraryNote = `Could not turn ${enabled ? "on" : "off"} ${song.title}: ${(e as Error).message}`;
  }
  renderSongs();
}

async function deleteSong(song: LibraryListing): Promise<void> {
  if (!window.confirm(`Delete "${song.title}" from the hub? This can't be undone (you can add the file again).`)) return;
  if (player.current?.track.id === song.id) stopSong();
  try {
    await library.remove(song.id);
    songs = songs.filter((s) => s.id !== song.id);
    analyses.delete(song.id);
    buffers.delete(song.id);
    songState.delete(song.id);
  } catch (e) {
    libraryNote = `Could not delete ${song.title}: ${(e as Error).message}`;
  }
  renderSongs();
}

async function uploadFiles(files: File[]): Promise<void> {
  for (const file of files) {
    libraryNote = `Adding ${file.name}…`;
    renderSongs();
    try {
      await library.upload(file);
      libraryNote = "";
    } catch (e) {
      libraryNote = `Could not add ${file.name}: ${(e as Error).message}`;
    }
  }
  const note = libraryNote;
  await loadLibrary();
  libraryNote = note;
  await analyzeAll();
}

function renderSongs(): void {
  const playingId = player.current?.track.id;
  $("#songs").replaceChildren(
    ...songs.map((song, i) => {
      const state = songState.get(song.id);
      const name = el("button", { class: "song-name", title: "Play this song" }, song.title);
      name.addEventListener("click", () => void playSong(song));
      const detail = state
        ? el("span", { class: state.startsWith("error") ? "error" : "hint" }, state)
        : song.analysis
          ? el("span", { class: "hint" }, `${Math.round(song.analysis.bpm)} BPM · ${formatTime(song.analysis.duration)}`)
          : el("span", { class: "hint" }, "not analyzed yet");
      const rename = el("button", { class: "remove", title: "Rename this song", "aria-label": `Rename ${song.title}` }, "✎");
      rename.addEventListener("click", async () => {
        const title = window.prompt("Song title", song.title);
        if (!title || title === song.title) return;
        try {
          await library.rename(song.id, title);
          song.title = title;
          renderSongs();
        } catch (e) {
          libraryNote = `Could not rename: ${(e as Error).message}`;
          renderSongs();
        }
      });
      const up = el("button", { class: "remove", title: "Move up", "aria-label": `Move ${song.title} up` }, "↑");
      const down = el("button", { class: "remove", title: "Move down", "aria-label": `Move ${song.title} down` }, "↓");
      up.toggleAttribute("disabled", i === 0);
      down.toggleAttribute("disabled", i === songs.length - 1);
      up.addEventListener("click", () => void moveSong(song.id, -1));
      down.addEventListener("click", () => void moveSong(song.id, 1));
      const share = el(
        "button",
        {
          class: "remove",
          "aria-pressed": String(song.public),
          title: song.public
            ? "Plays on the public Listen page. Click to make it neighbors-only."
            : "Not on the public Listen page. Click to share it there.",
        },
        song.public ? "Public" : "Not public",
      );
      share.addEventListener("click", async () => {
        try {
          await library.setPublic(song.id, !song.public);
          song.public = !song.public;
          renderSongs();
        } catch (e) {
          libraryNote = `Could not change sharing: ${(e as Error).message}`;
          renderSongs();
        }
      });
      const onOff = el(
        "button",
        {
          class: "remove",
          "aria-pressed": String(song.enabled),
          title: song.enabled
            ? "In the playlist. Click to turn it off (Play, Next and the show schedule skip it)."
            : "Off: Play, Next and the show schedule skip it. Click to turn it back on.",
        },
        song.enabled ? "On" : "Off",
      );
      onOff.addEventListener("click", () => void setSongEnabled(song, !song.enabled));
      const remove = el("button", { class: "remove", title: "Delete this song", "aria-label": `Delete ${song.title}` }, "🗑");
      remove.addEventListener("click", () => void deleteSong(song));
      const classes = [song.id === playingId ? "playing" : "", song.enabled ? "" : "off"].filter(Boolean).join(" ");
      return el("li", { class: classes }, name, detail, onOff, share, rename, up, down, remove);
    }),
  );
  const pending = songs.filter((s) => !s.analysis).length;
  const analyze = $<HTMLButtonElement>("#song-analyze");
  analyze.disabled = analyzingAll || pending === 0;
  analyze.textContent = analyzingAll ? "Analyzing…" : pending ? `Analyze all (${pending})` : "All analyzed";
  renderSongStatus();
}

function renderSongStatus(): void {
  const playing = player.current;
  $("#song-status").textContent = libraryNote
    ? libraryNote
    : playing
      ? `Playing ${playing.track.title} · ${formatTime(player.position())} / ${formatTime(playing.track.analysis.duration)}`
      : songs.length
        ? `${songs.length} song${songs.length === 1 ? "" : "s"} in the library${offNote()}. Press Play or click a song.`
        : "The library is empty. Add song files.";
}

function offNote(): string {
  const off = songs.filter((s) => !s.enabled).length;
  return off ? ` (${off} turned off)` : "";
}

// Keep the hub's beat clock on the song's real beats.
function syncSongBeat(force = false): void {
  if (!player.current) return;
  const now = Date.now();
  // Beats from when a new show could take effect (hub lead ~300 ms) onward.
  let beats = player.beatsBetween(now + 400, now + 400 + SONG_LOOKAHEAD_MS);
  let grid = fitBeatGrid(beats);
  if (!grid) return;
  // Beyond the accepted tempo range, follow every other beat.
  if (60000 / grid.beatMs > LIMITS.maxBpm) {
    beats = beats.filter((_, i) => i % 2 === 0);
    grid = fitBeatGrid(beats);
    if (!grid) return;
  }
  const bpm = Math.round((60000 / grid.beatMs) * 1000) / 1000;
  if (bpm < LIMITS.minBpm || bpm > LIMITS.maxBpm) return;

  if (!force && now - lastSongSend < SONG_MIN_RESEND_MS) return;
  // Keep whichever effect and speed the admin picked, on the song's beats.
  const spec = effectSpec(isEffect(ui.mode) ? ui.mode : "step", bpm, Math.round(grid.firstMs));
  const cur = hubShow;
  if (!force && cur && isBeatShow(cur) && sameEffect(cur, spec) && sameColors(cur.palette, ui.palette)) {
    const start = cur.startEpochMs ?? cur.t;
    const worst = Math.max(...beats.map((b) => Math.abs(offGrid(b, start, cur.bpm))));
    if (worst <= SONG_MAX_ERROR_MS) return;
  }
  lastSongSend = now;
  beatEffect();
  ui.bpm = bpm;
  $<HTMLInputElement>("#bpm").value = bpm.toFixed(1);
  sendControl({ type: "set_show", show: spec });
}

function setupSongControls(): void {
  const input = $<HTMLInputElement>("#song-files");
  input.addEventListener("change", () => {
    if (input.files?.length) void uploadFiles([...input.files]);
    input.value = "";
  });
  const drop = $("#drop");
  drop.addEventListener("dragover", (e) => {
    e.preventDefault();
    drop.classList.add("over");
  });
  drop.addEventListener("dragleave", () => drop.classList.remove("over"));
  drop.addEventListener("drop", (e) => {
    e.preventDefault();
    drop.classList.remove("over");
    const files = [...(e.dataTransfer?.files ?? [])].filter((f) =>
      /\.(mp3|m4a|aac|wav|ogg|oga|opus|flac)$/i.test(f.name),
    );
    if (files.length) void uploadFiles(files);
  });
  $("#song-play").addEventListener("click", () => {
    if (player.current) return;
    const first = songs.find(playable);
    if (first) void playSong(first);
  });
  $("#song-next").addEventListener("click", () => {
    const current = player.current?.track.id;
    const next = current ? songAfter(current) : songs.find(playable);
    if (next) void playSong(next);
  });
  $("#song-stop").addEventListener("click", stopSong);
  $("#song-analyze").addEventListener("click", () => void analyzeAll());

  // Timers slow to about once a second in a background tab; that is enough,
  // because every show carries absolute times.
  setInterval(() => {
    syncSongBeat();
    renderSongStatus();
  }, 500);
}

// ---------------------------------------------------------------------------
// Simulated houses

// Each simulated house has two strands: a roofline and a bush.
const ROOF_BULBS = 16;
const BUSH_BULBS = 10;

interface SimStrand {
  id: string;
  bulbs: HTMLElement[];
}

class DomDriver implements OutputDriver {
  readonly name = "simulator";
  latencyMs = 0;
  // The bush can play a strand without real-time mode, to show what such
  // lights get instead of per-section effects.
  bushRealtime = true;
  private readonly strands: SimStrand[];

  constructor(strands: SimStrand[]) {
    this.strands = strands;
  }

  async start(): Promise<void> {}

  outputs(): Output[] {
    return this.strands.map((s, i) => {
      const realtime = i === 0 || this.bushRealtime;
      return { id: s.id, realtime, sections: realtime ? LIMITS.maxSections : 1 };
    });
  }

  apply(frames: Map<string, Frame>): void {
    for (const [id, frame] of frames) {
      const strand = this.strands.find((s) => s.id === id);
      if (strand) setTimeout(() => this.paint(strand, frame), this.latencyMs);
    }
  }

  // Spreads the sections across the strand's bulbs, like a real strand.
  private paint(strand: SimStrand, frame: Frame): void {
    const segs = frame.segments;
    strand.bulbs.forEach((bulb, i) => {
      const c = segs?.length ? (segs[Math.floor((i * segs.length) / strand.bulbs.length)] ?? frame.rgb) : frame.rgb;
      const level = frame.on ? (frame.brightness / 100) * (segs ? Math.max(...c) / 255 : 1) : 0;
      bulb.style.setProperty("--bulb", frame.on ? css(c) : "#2a2f40");
      bulb.style.setProperty("--glow", level.toFixed(3));
    });
  }

  devices(): DeviceInfo[] {
    return this.strands.map((s) => ({ id: s.id, sku: "SIMULATED", lastSeen: Date.now() }));
  }

  async stop(): Promise<void> {}
}

interface SimHouse {
  id: string;
  agent: AgentClient;
  driver: DomDriver;
  card: HTMLElement;
  unplugged: boolean;
  socket: WebSocket | null;
}

const sims: SimHouse[] = [];

function addSimHouse(): void {
  const n = sims.length + 1;
  const id = `sim-${n}`;
  const bulbs = (count: number) => Array.from({ length: count }, () => el("span", { class: "bulb" }));
  const roof = { id: `${id}-roof`, bulbs: bulbs(ROOF_BULBS) };
  const bush = { id: `${id}-bush`, bulbs: bulbs(BUSH_BULBS) };
  const dot = el("span", { class: "dot", title: "hub connection" });
  const lag = el("input", { type: "range", min: "0", max: "800", step: "20", value: "0" });
  const lagValue = el("output", {}, "0 ms");
  const unplug = el("input", { type: "checkbox" });
  const plainBush = el("input", { type: "checkbox" });
  const card = el(
    "div",
    { class: "house" },
    el("div", { class: "house-top" }, el("strong", {}, id), el("span", {}, `pos ${n - 1} `, dot)),
    el("div", { class: "strand", title: "Roofline (strand 1)" }, ...roof.bulbs),
    el("div", { class: "roof" }),
    el("div", { class: "strand bush", title: "Bush (strand 2)" }, ...bush.bulbs),
    el("label", {}, el("span", {}, "Light lag ", lagValue), lag),
    el("label", { class: "check" }, unplug, "Unplug internet"),
    el("label", { class: "check" }, plainBush, "Bush without real-time mode"),
  );
  $("#street").append(card);

  const driver = new DomDriver([roof, bush]);
  plainBush.addEventListener("change", () => {
    driver.bushRealtime = !plainBush.checked;
  });
  const sim: SimHouse = { id, agent: null as unknown as AgentClient, driver, card, unplugged: false, socket: null };

  // A WebSocket that goes nowhere while the house is "unplugged".
  class SimSocket extends WebSocket {
    constructor(url: string | URL) {
      super(sim.unplugged ? "ws://127.0.0.1:9/unplugged" : url);
      sim.socket = this;
    }
  }

  sim.agent = new AgentClient({
    hubUrl: settings.hub,
    room: settings.room,
    key: settings.agentKey,
    house: id,
    position: n - 1,
    driver,
    reconnectMaxMs: 4000,
    WebSocketImpl: SimSocket,
    log: (line) => console.log(`[${id}] ${line}`),
    onConnectionChange: (connected) => dot.classList.toggle("ok", connected),
  });

  lag.addEventListener("input", () => {
    driver.latencyMs = Number(lag.value);
    lagValue.textContent = `${lag.value} ms`;
  });
  unplug.addEventListener("change", () => {
    sim.unplugged = unplug.checked;
    if (sim.unplugged) sim.socket?.close();
  });

  sims.push(sim);
  sim.agent.start();
}

function removeSimHouse(): void {
  const sim = sims.pop();
  if (!sim) return;
  sim.agent.stop();
  sim.card.remove();
}

function restartSims(): void {
  const count = sims.length;
  while (sims.length) removeSimHouse();
  for (let i = 0; i < count; i++) addSimHouse();
}

// ---------------------------------------------------------------------------
// Houses table

const rows = new Map<string, { row: HTMLTableRowElement; slider: HTMLInputElement; value: HTMLElement }>();

function renderHouses(houses: HouseView[]): void {
  const tbody = $("#houses tbody");
  $("#no-houses").hidden = houses.length > 0;
  const seen = new Set<string>();

  for (const h of houses) {
    seen.add(h.house);
    let entry = rows.get(h.house);
    if (!entry) {
      const slider = el("input", {
        type: "range",
        min: "-1000",
        max: "1000",
        step: "10",
        "aria-label": `Offset for ${h.house}`,
      });
      const value = el("output");
      slider.addEventListener("input", () => {
        value.textContent = `${slider.value} ms`;
      });
      slider.addEventListener("change", () => {
        sendControl({ type: "set_offset", house: h.house, offsetMs: Number(slider.value) });
      });
      const row = el("tr", {}, ...Array.from({ length: 6 }, () => el("td")), el("td", {}, el("div", { class: "offset" }, slider, value)));
      entry = { row, slider, value };
      rows.set(h.house, entry);
    }
    const cells = entry.row.cells;
    const skew = h.clockSkewMs;
    cells[0]!.textContent = h.house;
    cells[1]!.textContent = String(h.position);
    cells[2]!.replaceChildren(
      el("span", { class: `dot${h.connected ? " ok" : ""}` }),
      ` ${h.connected ? "online" : `offline, seen ${ago(h.lastSeen)}`}`,
    );
    cells[3]!.textContent = h.driver;
    cells[4]!.textContent = h.devices.length ? h.devices.map((d) => d.sku + (d.ip ? ` @ ${d.ip}` : "")).join(", ") : "none found";
    cells[5]!.textContent = skew === null ? "–" : `${skew > 0 ? "+" : ""}${skew} ms`;
    cells[5]!.className = skew !== null && Math.abs(skew) > 100 ? "warn" : "";
    if (document.activeElement !== entry.slider) {
      entry.slider.value = String(h.offsetMs);
      entry.value.textContent = `${h.offsetMs} ms`;
    }
    tbody.append(entry.row); // keeps hub order (by street position)
  }
  for (const [house, entry] of rows) {
    if (!seen.has(house)) {
      entry.row.remove();
      rows.delete(house);
    }
  }
}

// ---------------------------------------------------------------------------
// Startup

function setupConnectionForm(): void {
  const form = $<HTMLFormElement>("#connect-form");
  for (const [k, v] of Object.entries(settings)) {
    const input = form.elements.namedItem(k) as HTMLInputElement | null;
    if (input) input.value = v;
  }
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const data = new FormData(form);
    settings = {
      hub: String(data.get("hub")).trim(),
      room: String(data.get("room")).trim(),
      controlKey: String(data.get("controlKey")),
      agentKey: String(data.get("agentKey")),
    };
    saveSettings(settings);
    connectController();
    restartSims();
  });
}

function startConsole(withSims: boolean): void {
  $("#console").hidden = false;
  setupShowControls();
  setupListenControls();
  setupSongControls();
  renderShowControls();
  connectController();
  if (withSims) {
    $("#sim-panel").hidden = false;
    for (let i = 0; i < 3; i++) addSimHouse();
    $("#add-house").addEventListener("click", addSimHouse);
    $("#remove-house").addEventListener("click", removeSimHouse);
  }
}


// The Pis install only light-helper releases the admin signed on their Mac
// (security.md S1). Say so when the code has moved on since the last one.
async function showReleaseBanner(): Promise<void> {
  let status: { release: number | null; changed: string[] } | null;
  try {
    status = await api("GET", "/api/admin/agent-release");
  } catch {
    return;
  }
  if (!status || (status.release !== null && !status.changed.length)) return;
  const text =
    status.release === null
      ? "No signed light-helper release yet, so Pis can't be set up. On your Mac: npm run sign-agent, then commit release/ and push (admin guide, C6)."
      : `The Pis are on light-helper release ${status.release}, and the code changed since. ` +
        "On your Mac: npm run sign-agent, then commit release/ and push (admin guide, C6).";
  const banner = el("section", { class: "panel", role: "status", style: "background:var(--gold-soft)" }, text);
  $("#street-admin").prepend(banner);
}

async function main(): Promise<void> {
  const me = await getMe();
  await setupHeader(me);
  if (!me) {
    // Development hub: room keys and simulated houses.
    $("#connection").hidden = false;
    $("#dev-houses").hidden = false;
    setupConnectionForm();
    startConsole(true);
    return;
  }
  if (!me.signedIn || !me.admin) {
    // Sign in happens on the neighborhood page.
    location.href = "/neighborhood";
    return;
  }
  streetMode = true;
  // Same site as this page; the session cookie authenticates.
  settings = { ...settings, hub: `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}`, room: "street", controlKey: "" };
  setupStreetAdmin();
  setupSchedule();
  void showReleaseBanner();
  const sims = new URLSearchParams(location.search).has("sim");
  if (sims) $("#dev-houses").hidden = false;
  startConsole(sims);
}

void main();
